import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useAuth } from '../services/auth';

const UserProfileModal = ({ open, onClose }) => {
  const { user, changePassword, updateProfile } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    fullName: '',
    email: '',
    username: ''
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Update profile form when user data changes
  useEffect(() => {
    setProfileForm({
      fullName: user?.FullName || '',
      email: user?.Email || '',
      username: user?.Username || ''
    });
  }, [user?.FullName, user?.Email, user?.Username]);

  const handleClose = () => {
    setError('');
    setSuccess('');
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setProfileForm({
      fullName: user?.FullName || '',
      email: user?.Email || '',
      username: user?.Username || ''
    });
    onClose();
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      setError('Please fill in all password fields');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setSuccess('Password changed successfully!');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!profileForm.username || !profileForm.email || !profileForm.fullName) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await updateProfile({
        username: profileForm.username,
        email: profileForm.email,
        fullName: profileForm.fullName
      });
      setSuccess('Profile information updated successfully!');
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(145deg, #1A1F2E 0%, #2A2F3E 100%)',
          border: '1px solid rgba(0, 191, 255, 0.2)',
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon color="primary" />
          <Typography variant="h6">User Profile</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Profile Information Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Profile Information
          </Typography>

          <Box sx={{ mb: 2 }}>
            <TextField
              label="Username"
              value={profileForm.username}
              onChange={(e) => setProfileForm(prev => ({ ...prev, username: e.target.value }))}
              fullWidth
              size="small"
              helperText="Enter your username"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              label="Email"
              value={profileForm.email}
              onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
              fullWidth
              size="small"
              type="email"
              helperText="Enter your email address"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              label="Full Name"
              value={profileForm.fullName}
              onChange={(e) => setProfileForm(prev => ({ ...prev, fullName: e.target.value }))}
              fullWidth
              size="small"
              helperText="Enter your full display name"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              label="Role"
              value={user?.Role || ''}
              disabled
              fullWidth
              size="small"
              helperText="Role is assigned by administrators"
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Change Password Section */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <LockIcon fontSize="small" />
              Change Password
            </Box>
          </Typography>

          <Box sx={{ mb: 2 }}>
            <TextField
              label="Current Password"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
              fullWidth
              size="small"
              autoComplete="current-password"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
              fullWidth
              size="small"
              autoComplete="new-password"
              helperText="Minimum 6 characters"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              label="Confirm New Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
              fullWidth
              size="small"
              autoComplete="new-password"
            />
          </Box>

          <Button
            variant="outlined"
            onClick={handlePasswordChange}
            disabled={loading || !passwordForm.currentPassword || !passwordForm.newPassword}
            fullWidth
            sx={{
              mb: 1,
              color: '#1A1F2E',
              borderColor: '#1A1F2E',
              '&:hover': {
                borderColor: '#0A0E1A',
                backgroundColor: 'rgba(26, 31, 46, 0.1)'
              }
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#1A1F2E' }} /> : 'Change Password'}
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ color: '#1a237e' }}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdateProfile}
          disabled={loading}
          sx={{
            backgroundColor: 'transparent',
            color: '#1A1F2E',
            border: '1px solid #1A1F2E',
            '&:hover': {
              backgroundColor: 'rgba(26, 31, 46, 0.1)',
            }
          }}
        >
          Update Profile
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserProfileModal;